import * as React from 'react';
import { APIProvider, Map, useMap, useApiLoadingStatus } from '@vis.gl/react-google-maps';

import { log } from '../../utils/logger';
import { MapViewWeb, MapViewWebRegion } from './MapViewWeb';

// Inject the real @vis.gl/react-google-maps dependencies into MapViewWeb through its explicit
// `googleMaps` seam, mirroring how MapView.native.js injects react-native-maps as `mapViewImpl`
// into MapViewMobile. Passing the whole bundle from one import keeps these in the same module
// instance as MapViewWeb's re-exported AdvancedMarker, preserving the Google Maps context.
const googleMaps = { APIProvider, Map, useMap, useApiLoadingStatus };

/**
 * MapView provides a consistent abstraction of the platform-specific mobile (React Native) and web
 * (@vis.gl/react-google-maps) implementations. In particular, the distinction between deltas (React Native) and zoom level
 * (web) is maintained within a MapViewRegion that should always be treated as opaque.
 */
export function MapView(props) {
  const { style, initialRegion, mapViewRef, onRegionChangeComplete, onPress, children } = props;

  const mapViewImplRef = React.useRef(null);

  // TODO: Define a TypeScript interface for the MapView ref (e.g. { animateToRegion(region): void })
  // to enforce at compile time that both MapViewMobile and MapViewWeb provide the required API.
  React.useImperativeHandle(
    mapViewRef,
    () =>
      // Wrap the MapView ref to constrain the interface available to the parent container to the
      // platform-agnostic abstraction that this component provides. Because of the differences between
      // the React Native and web (@vis.gl/react-google-maps) implementations, this is intentionally a
      // minimal interface of only what is absolutely needed.
      //
      // https://react.dev/reference/react/useImperativeHandle
      ({
        animateToRegion(mapViewRegion) {
          mapViewImplRef.current?.animateToRegion(mapViewRegion);
        },
      }),
    []
  );

  return (
    <MapViewWeb
      initialRegion={initialRegion}
      mapViewRef={mapViewImplRef}
      onRegionChangeComplete={onRegionChangeComplete}
      onPress={onPress}
      style={style}
      googleMaps={googleMaps}
    >
      {children}
    </MapViewWeb>
  );
}

export function makeMapViewRegion({ latitude, latitudeDelta, longitude, longitudeDelta }) {
  const mapViewRegion = new MapViewWebRegion(latitude, latitudeDelta, longitude, longitudeDelta);

  log('MapView.makeMapViewRegion()');

  return mapViewRegion;
}
